import { useEffect, useMemo, useRef, useState } from "react"
import { Code2, ExternalLink, Monitor, MousePointer2, Smartphone, Tablet } from "lucide-react"
import { parseAsString, parseAsStringLiteral, useQueryState } from "nuqs"
import { useHotkeys } from "react-hotkeys-hook"

import {
  Badge,
  Button,
  Input,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@aix/ui"

import { trpc } from "../lib/trpc"
import { useDebouncedValue } from "../lib/filters/useUrlFilters"

type PreviewSource = "sample" | "draft"
type PreviewViewport = "desktop" | "tablet" | "mobile"
type FontRegistrationStatus = "loaded" | "not-loaded" | "unregistered"

interface ComputedTypography {
  fontFamily: string
  fontSize: string
  fontWeight: string
  letterSpacing: string
  lineHeight: string
  bodyFontStatus: FontRegistrationStatus
  displayFontStatus: FontRegistrationStatus
}

const VIEWPORT_WIDTHS: Record<PreviewViewport, string> = {
  desktop: "100%",
  tablet: "768px",
  mobile: "390px",
}

const TEMPLATE_LAB_STYLE = `
[data-seo-slot]{outline:2px dashed rgba(112,87,217,.7);outline-offset:2px;cursor:crosshair;transition:outline-color .12s ease,background .12s ease}
[data-seo-slot]:hover{outline-color:#ef476f;background:rgba(239,71,111,.08)}
[data-seo-slot][data-seo-lab-selected=true]{outline:4px solid #ef476f;background:rgba(239,71,111,.13)}
`

function valueAtPath(value: unknown, path: string): unknown {
  const keys = path.replace(/\[(\d+)\]/g, ".$1").split(".")
  let current = value
  for (const key of keys) {
    if (typeof current !== "object" || current === null || !(key in current)) return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

function displayedValue(value: unknown, path: string): string {
  if (value === undefined && path.startsWith("shell.")) return "Owned by the shared VidMax shell"
  if (value === undefined) return "No structured value exists at this path"
  if (typeof value === "string") return value
  return JSON.stringify(value, null, 2) ?? "This value cannot be displayed"
}

function slotContract(path: string, required: boolean | null): string {
  if (path.startsWith("shell.")) return "Shared VidMax shell"
  if (required === true) return "Required"
  if (required === false) return "Optional"
  return "Generated layout"
}

function fontRegistrationStatus(fonts: FontFaceSet, family: string | undefined): FontRegistrationStatus {
  if (!family) return "unregistered"
  const normalizedFamily = family.replaceAll(/["']/g, "").trim().toLocaleLowerCase()
  let registered = false
  let loaded = false
  fonts.forEach((face) => {
    const faceFamily = face.family.replaceAll(/["']/g, "").trim().toLocaleLowerCase()
    if (faceFamily !== normalizedFamily) return
    registered = true
    if (face.status === "loaded") loaded = true
  })
  if (!registered) return "unregistered"
  return loaded ? "loaded" : "not-loaded"
}

export default function SEOTemplateLabTab({ productKey }: { productKey: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [selectedTemplateVersion, setSelectedTemplateVersion] = useQueryState("template", parseAsString.withDefault(""))
  const [source, setSource] = useQueryState(
    "source",
    parseAsStringLiteral(["sample", "draft"] as const).withDefault("sample"),
  )
  const [viewport, setViewport] = useQueryState(
    "viewport",
    parseAsStringLiteral(["desktop", "tablet", "mobile"] as const).withDefault("desktop"),
  )
  const [selectedDraftSlug, setSelectedDraftSlug] = useQueryState("draft", parseAsString.withDefault(""))
  const [selectedSlot, setSelectedSlot] = useState("h1")
  const [selectedSlotRequired, setSelectedSlotRequired] = useState<boolean | null>(true)
  const [computedTypography, setComputedTypography] = useState<ComputedTypography | null>(null)
  const [draftSearch, setDraftSearch] = useState("")
  const debouncedDraftSearch = useDebouncedValue(draftSearch.trim(), 250)

  const catalog = trpc.seo.templates.catalog.useQuery({ productKey })
  const selectedTemplate = catalog.data?.templates.find(
    (template) => template.templateVersion === selectedTemplateVersion,
  )
  const pages = trpc.seo.pages.list.useQuery({
    productKey,
    templateVersions: selectedTemplate ? [...selectedTemplate.compatibleDraftTemplateVersions] : undefined,
    contentKind: selectedTemplate?.contentKind,
    query: debouncedDraftSearch || undefined,
    limit: 100,
    offset: 0,
  })
  const matchingDrafts = useMemo(() => pages.data?.rows ?? [], [pages.data?.rows])
  const draft = trpc.seo.pages.get.useQuery(
    { productKey, slug: selectedDraftSlug },
    { enabled: source === "draft" && Boolean(selectedDraftSlug) },
  )
  const draftPreview = trpc.seo.pages.preview.useQuery(
    { productKey, slug: selectedDraftSlug },
    { enabled: source === "draft" && Boolean(selectedDraftSlug) },
  )

  useEffect(() => {
    const templates = catalog.data?.templates ?? []
    const first = catalog.data?.templates[0]
    if (!templates.some((template) => template.templateVersion === selectedTemplateVersion) && first) {
      void setSelectedTemplateVersion(first.templateVersion)
    }
  }, [catalog.data?.templates, selectedTemplateVersion, setSelectedTemplateVersion])

  useEffect(() => {
    const first = matchingDrafts[0]
    if (source === "draft" && !matchingDrafts.some((page) => page.slug === selectedDraftSlug)) {
      void setSelectedDraftSlug(first?.slug ?? "")
    }
  }, [matchingDrafts, selectedDraftSlug, setSelectedDraftSlug, source])

  const activePreview = source === "draft" ? draftPreview.data : selectedTemplate?.preview
  const activeContent = source === "draft" ? draft.data?.content : selectedTemplate?.content
  const selectedValue = displayedValue(valueAtPath(activeContent, selectedSlot), selectedSlot)
  const draftError = pages.error?.message ?? draft.error?.message ?? draftPreview.error?.message

  const moveTemplate = (direction: 1 | -1) => {
    const templates = catalog.data?.templates ?? []
    if (!templates.length) return
    const currentIndex = Math.max(
      0,
      templates.findIndex((template) => template.templateVersion === selectedTemplateVersion),
    )
    const nextIndex = Math.max(0, Math.min(templates.length - 1, currentIndex + direction))
    void setSelectedTemplateVersion(templates[nextIndex].templateVersion)
    void setSource("sample")
    setSelectedSlot("h1")
    setSelectedSlotRequired(true)
  }

  useHotkeys("j, down", () => moveTemplate(1), { preventDefault: true, enableOnFormTags: false }, [
    catalog.data?.templates,
    selectedTemplateVersion,
  ])
  useHotkeys("k, up", () => moveTemplate(-1), { preventDefault: true, enableOnFormTags: false }, [
    catalog.data?.templates,
    selectedTemplateVersion,
  ])
  useHotkeys("1", () => void setViewport("desktop"), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("2", () => void setViewport("tablet"), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("3", () => void setViewport("mobile"), { preventDefault: true, enableOnFormTags: false })

  const preparePreview = () => {
    const document = iframeRef.current?.contentDocument
    if (!document) return
    const style = document.createElement("style")
    style.dataset.seoTemplateLab = "true"
    style.textContent = TEMPLATE_LAB_STYLE
    document.head.append(style)
    document.querySelectorAll<HTMLElement>("[data-seo-slot]").forEach((element) => {
      element.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        document.querySelectorAll("[data-seo-lab-selected]").forEach((selected) => {
          selected.removeAttribute("data-seo-lab-selected")
        })
        element.dataset.seoLabSelected = "true"
        setSelectedSlot(element.dataset.seoSlot ?? "")
        setSelectedSlotRequired(
          element.dataset.seoRequired === undefined ? null : element.dataset.seoRequired === "true",
        )
        const computed = document.defaultView?.getComputedStyle(element)
        if (!computed) return
        setComputedTypography({
          fontFamily: computed.fontFamily,
          fontSize: computed.fontSize,
          fontWeight: computed.fontWeight,
          letterSpacing: computed.letterSpacing,
          lineHeight: computed.lineHeight,
          bodyFontStatus: fontRegistrationStatus(document.fonts, selectedTemplate?.typography.body),
          displayFontStatus: fontRegistrationStatus(document.fonts, selectedTemplate?.typography.display),
        })
      })
    })
    const initialElement = document.querySelector<HTMLElement>('[data-seo-slot="h1"]')
    initialElement?.click()
  }

  if (catalog.error) {
    return <div className="p-6 text-sm text-destructive">Template Lab failed to load: {catalog.error.message}</div>
  }
  if (catalog.isLoading) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">Loading the production template registry…</div>
    )
  }
  if (!catalog.data?.templates.length) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        Template Lab is not registered for {productKey} yet.
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-muted/20">
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-background px-3 py-2">
        <Select value={selectedTemplateVersion} onValueChange={(value) => void setSelectedTemplateVersion(value)}>
          <SelectTrigger className="h-9 w-64" aria-label="Template family">
            <SelectValue placeholder="Choose template" />
          </SelectTrigger>
          <SelectContent>
            {catalog.data.templates.map((template) => (
              <SelectItem key={template.templateVersion} value={template.templateVersion}>
                {template.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={(value) => void setSource(value as PreviewSource)}>
          <SelectTrigger className="h-9 w-40" aria-label="Preview source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sample">Sample contract</SelectItem>
            <SelectItem value="draft" disabled={matchingDrafts.length === 0}>
              Current draft
            </SelectItem>
          </SelectContent>
        </Select>
        {source === "draft" ? (
          <>
            <Input
              value={draftSearch}
              onChange={(event) => setDraftSearch(event.currentTarget.value)}
              placeholder="Search draft path…"
              aria-label="Search current drafts by path"
              className="h-9 w-52"
            />
            <Select value={selectedDraftSlug} onValueChange={(value) => void setSelectedDraftSlug(value)}>
              <SelectTrigger className="h-9 min-w-64 flex-1" aria-label="Current draft page">
                <SelectValue placeholder="Choose current draft" />
              </SelectTrigger>
              <SelectContent>
                {matchingDrafts.map((page) => (
                  <SelectItem key={page.slug} value={page.slug}>
                    {page.slug}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        ) : null}
        <div className="ml-auto flex items-center rounded-md border border-border bg-background p-1">
          <ViewportButton
            viewport="desktop"
            active={viewport}
            onClick={(next) => void setViewport(next)}
            icon={Monitor}
            label="Desktop"
          />
          <ViewportButton
            viewport="tablet"
            active={viewport}
            onClick={(next) => void setViewport(next)}
            icon={Tablet}
            label="Tablet"
          />
          <ViewportButton
            viewport="mobile"
            active={viewport}
            onClick={(next) => void setViewport(next)}
            icon={Smartphone}
            label="Mobile"
          />
        </div>
      </div>

      <ResizablePanelGroup direction="horizontal" autoSaveId="seo-template-lab-panes" className="flex-1">
        <ResizablePanel defaultSize={76} minSize={50} className="min-w-0">
          <div className="flex h-full flex-col p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary">{selectedTemplate?.label}</Badge>
              <span>{selectedTemplate?.description}</span>
              <span className="ml-auto font-mono">{activePreview?.rendererVersion}</span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border bg-muted p-3">
              <div
                className="mx-auto h-full overflow-hidden rounded border border-border bg-background shadow-sm transition-[width]"
                style={{ width: VIEWPORT_WIDTHS[viewport] }}
              >
                {source === "draft" && draftError ? (
                  <div className="m-4 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
                    Draft preview failed: {draftError}
                  </div>
                ) : source === "draft" && (pages.isLoading || draftPreview.isLoading) ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">Rendering current draft…</div>
                ) : activePreview ? (
                  <iframe
                    ref={iframeRef}
                    key={`${source}:${selectedTemplateVersion}:${selectedDraftSlug}:${viewport}`}
                    title={`${source === "sample" ? "Sample" : "Current draft"} ${selectedTemplate?.label}`}
                    srcDoc={activePreview.html}
                    sandbox="allow-same-origin"
                    onLoad={preparePreview}
                    className="h-full min-h-[640px] w-full bg-white"
                  />
                ) : (
                  <div className="p-8 text-center text-sm text-muted-foreground">Choose a draft to render.</div>
                )}
              </div>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={24} minSize={20} maxSize={40}>
          <aside
            className="h-full overflow-auto border-l border-border bg-background p-4"
            aria-label="Template slot inspector"
          >
            <div className="flex items-center gap-2">
              <MousePointer2 className="h-4 w-4 text-primary" />
              <h2 className="font-heading text-base font-semibold">Slot inspector</h2>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Click any purple outline in the real rendered page. Red marks the selected replacement.
            </p>

            <dl className="mt-5 space-y-4 text-sm">
              <InspectorField label="JSON path" value={selectedSlot || "Select a slot"} mono />
              <InspectorField label="Contract" value={slotContract(selectedSlot, selectedSlotRequired)} />
              <InspectorField label="Current value" value={selectedValue} mono />
              <InspectorField label="Template version" value={activePreview?.templateVersion ?? "—"} mono />
              <InspectorField label="Renderer version" value={activePreview?.rendererVersion ?? "—"} mono />
              <InspectorField label="Shared shell" value={selectedTemplate?.shellVersion ?? "—"} mono />
              <InspectorField
                label="Typography"
                value={`${selectedTemplate?.typography.body ?? "—"} body · ${selectedTemplate?.typography.display ?? "—"} display`}
              />
              <InspectorField
                label="Computed font"
                value={computedTypography?.fontFamily ?? "Select a rendered slot"}
                mono
              />
              <InspectorField
                label="Computed metrics"
                value={
                  computedTypography
                    ? `${computedTypography.fontSize} size · ${computedTypography.fontWeight} weight · ${computedTypography.lineHeight} line · ${computedTypography.letterSpacing} tracking`
                    : "—"
                }
                mono
              />
              <InspectorField
                label="Web fonts"
                value={
                  computedTypography
                    ? `${selectedTemplate?.typography.body ?? "Body"}: ${computedTypography.bodyFontStatus} · ${selectedTemplate?.typography.display ?? "Display"}: ${computedTypography.displayFontStatus}`
                    : "—"
                }
              />
            </dl>

            <div className="mt-5 rounded-md border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
              <div className="mb-1 flex items-center gap-1.5 font-medium text-foreground">
                <Code2 className="h-3.5 w-3.5" /> Where this is stored
              </div>
              Layout and shell live in the versioned VidMax renderer. Page values live in AIX structured content. Media
              references resolve to R2 assets at release compile time.
            </div>

            {activePreview?.canonicalUrl ? (
              <Button variant="outline" size="sm" className="mt-4 w-full" asChild>
                <a href={activePreview.canonicalUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1.5 h-4 w-4" /> Canonical URL
                </a>
              </Button>
            ) : null}

            <div className="mt-6 border-t border-border pt-4 text-[11px] text-muted-foreground">
              Shortcuts: <kbd className="font-mono">j/k</kbd> template · <kbd className="font-mono">1/2/3</kbd> viewport
            </div>
          </aside>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}

function InspectorField({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`mt-1 whitespace-pre-wrap break-words ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  )
}

function ViewportButton({
  active,
  icon: Icon,
  label,
  onClick,
  viewport,
}: {
  active: PreviewViewport
  icon: typeof Monitor
  label: string
  onClick: (viewport: PreviewViewport) => void
  viewport: PreviewViewport
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active === viewport ? "secondary" : "ghost"}
      onClick={() => onClick(viewport)}
      aria-label={`${label} preview`}
      title={`${label} (${viewport === "desktop" ? "1" : viewport === "tablet" ? "2" : "3"})`}
    >
      <Icon className="h-4 w-4" />
    </Button>
  )
}
